import React, { useState } from "react";
import { ChevronDown, Check } from "lucide-react";

const F = "currentColor";
const frame = (w = 44, h = 60, x = 8, y = 10) => <rect x={x} y={y} width={w} height={h} rx="2" fill="none" stroke={F} strokeWidth="3" />;
const line = (x1, y1, x2, y2, sw = 2.5, dash) => <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={F} strokeWidth={sw} strokeDasharray={dash} />;
// casement/awning "opening" mark: dashed V pointing to the hinge side
const vmark = (x, y, w, h, dir) => {
  const pts = dir === "left" ? `${x + w},${y} ${x},${y + h / 2} ${x + w},${y + h}`
    : dir === "right" ? `${x},${y} ${x + w},${y + h / 2} ${x},${y + h}`
    : dir === "down" ? `${x},${y} ${x + w / 2},${y + h} ${x + w},${y}`
    : `${x},${y + h} ${x + w / 2},${y} ${x + w},${y + h}`;
  return <polyline points={pts} fill="none" stroke={F} strokeWidth="2" strokeDasharray="3 3" />;
};
const arrow = (x1, y1, x2, y2) => (
  <g stroke={F} strokeWidth="2.5" fill="none" strokeLinecap="round">
    <line x1={x1} y1={y1} x2={x2} y2={y2} />
    {x1 === x2
      ? <polyline points={`${x2 - 4},${y2 + (y2 > y1 ? -5 : 5)} ${x2},${y2} ${x2 + 4},${y2 + (y2 > y1 ? -5 : 5)}`} />
      : <polyline points={`${x2 + (x2 > x1 ? -5 : 5)},${y2 - 4} ${x2},${y2} ${x2 + (x2 > x1 ? -5 : 5)},${y2 + 4}`} />}
  </g>
);

const ICONS = {
  double_hung: <>{frame()}{line(8, 40, 52, 40, 3)}{arrow(30, 34, 30, 16)}{arrow(30, 46, 30, 64)}</>,
  single_hung: <>{frame()}{line(8, 40, 52, 40, 3)}{arrow(30, 46, 30, 64)}</>,
  glider: <>{frame()}{line(30, 10, 30, 70, 3)}{arrow(14, 40, 26, 40)}{arrow(46, 40, 34, 40)}</>,
  glider_triple: <>{frame()}{line(22, 10, 22, 70, 3)}{line(38, 10, 38, 70, 3)}{arrow(12, 40, 19, 40)}{arrow(48, 40, 41, 40)}</>,
  casement: <>{frame()}{vmark(13, 15, 34, 50, "left")}</>,
  casement_double: <>{frame()}{line(30, 10, 30, 70, 3)}{vmark(11, 16, 16, 48, "left")}{vmark(33, 16, 16, 48, "right")}</>,
  casement_triple: <>{frame()}{line(22, 10, 22, 70, 3)}{line(38, 10, 38, 70, 3)}{vmark(10, 18, 10, 44, "left")}{vmark(40, 18, 10, 44, "right")}</>,
  awning: <>{frame()}{vmark(14, 18, 32, 44, "down")}</>,
  picture: <>{frame()}</>,
  fixed: <>{frame()}{line(8, 10, 52, 70, 1.5, "4 4")}{line(52, 10, 8, 70, 1.5, "4 4")}</>,
  specialty: <><path d="M8 70 V36 A22 22 0 0 1 52 36 V70 Z" fill="none" stroke={F} strokeWidth="3" />{line(8, 36, 52, 36, 2)}</>,
  transom: <>{frame(50, 20, 5, 30)}</>,
  sidelight: <>{frame(16, 64, 22, 8)}{line(22, 30, 38, 30, 2)}{line(22, 50, 38, 50, 2)}</>,
  entry: <>{frame(36, 64, 12, 8)}<circle cx="40" cy="42" r="2.5" fill={F} /><rect x="18" y="14" width="24" height="18" fill="none" stroke={F} strokeWidth="2" /></>,
  french: <>{frame(44, 64, 8, 8)}{line(30, 8, 30, 72, 3)}<rect x="12" y="13" width="14" height="40" fill="none" stroke={F} strokeWidth="2" /><rect x="34" y="13" width="14" height="40" fill="none" stroke={F} strokeWidth="2" /><circle cx="26" cy="44" r="2" fill={F} /><circle cx="34" cy="44" r="2" fill={F} /></>,
  sliding: <>{frame(48, 64, 6, 8)}{line(30, 8, 30, 72, 3)}{arrow(14, 40, 26, 40)}<rect x="34" y="12" width="16" height="56" fill="none" stroke={F} strokeWidth="1.5" /></>,
  patio: <>{frame(48, 64, 6, 8)}{line(30, 8, 30, 72, 3)}<rect x="10" y="12" width="16" height="56" fill="none" stroke={F} strokeWidth="1.5" /><rect x="34" y="12" width="16" height="56" fill="none" stroke={F} strokeWidth="1.5" /><circle cx="27" cy="42" r="2" fill={F} /></>,
  // storm door: full-view glass panel + kick plate + closer bar
  storm: <>{frame(36, 64, 12, 8)}<rect x="17" y="13" width="26" height="40" fill="none" stroke={F} strokeWidth="2" />{line(17, 60, 43, 60, 3)}{line(20, 20, 40, 20, 1.5, "3 3")}<circle cx="40" cy="44" r="2.5" fill={F} /></>,
};

export function WindowTypeIcon({ type, className = "h-10 w-8" }) {
  return (
    <svg viewBox="0 0 60 80" className={className} aria-hidden="true">
      {ICONS[type] || <>{frame()}<text x="30" y="46" textAnchor="middle" fontSize="22" fill={F}>?</text></>}
    </svg>
  );
}

/** Visual picker: every type with its drawing (tap the current one to expand the grid). */
export function WindowTypePicker({ options, groups, value, onChange, noneLabel, t }) {
  const [open, setOpen] = useState(!value);
  const sections = groups || [[null, options || []]];
  const all = sections.flatMap(([, opts]) => opts);
  const current = all.find(([v]) => v === value);
  const tile = ([v, label]) => (
    <button key={v} type="button" onClick={() => { onChange(v); setOpen(false); }} data-testid={`measure-subtype-${v}`}
            className={`relative rounded-lg border p-2 text-[11px] leading-tight flex flex-col items-center gap-1 ${value === v ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted/60"}`}>
      <WindowTypeIcon type={v} className="h-10 w-8" />
      <span className="text-center">{label}</span>
      {value === v && <Check className="absolute top-1 right-1 h-3.5 w-3.5 text-primary" />}
    </button>
  );
  return (
    <div className="space-y-2" data-testid="measure-type-picker">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="measure-subtype-select"
              className="w-full flex items-center gap-2 rounded-lg border bg-background px-2.5 py-1.5 text-left">
        {current ? <WindowTypeIcon type={current[0]} className="h-8 w-6 text-primary shrink-0" /> : <span className="h-8 w-6 shrink-0" />}
        <span className="text-sm flex-1 truncate">{current ? current[1] : noneLabel}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-2" data-testid="measure-type-grid">
          {sections.map(([title, opts], i) => (
            <div key={title || i} className="space-y-1">
              {title && <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-0.5" data-testid={`measure-type-group-${i}`}>{title}</div>}
              <div className="grid grid-cols-3 gap-1.5">
                {i === 0 && (
                  <button type="button" onClick={() => { onChange(""); setOpen(false); }} data-testid="measure-subtype-none"
                          className={`rounded-lg border p-2 text-[11px] leading-tight flex flex-col items-center gap-1 ${!value ? "border-primary bg-primary/10" : "hover:bg-muted/60"}`}>
                    <span className="h-10 w-8 flex items-center justify-center text-muted-foreground text-lg">—</span>
                    <span className="text-center">{noneLabel}</span>
                  </button>
                )}
                {opts.map(tile)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
